import { ShieldCheck, PackageCheck, CalendarClock, BadgeIndianRupee, MessageCircle } from "lucide-react";
import { SectionLabel } from "@/components/Reveal";
import RollingText from "@/components/RollingText";
import { BATCHES, CONTACT } from "@/lib/site";

export default function AuthenticBatches() {
  return (
    <section
      id="authenticity"
      data-testid="authenticity-section"
      className="relative z-10 py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid lg:grid-cols-[0.45fr_0.55fr] gap-10 lg:gap-20 items-end mb-14">
          <div>
            <SectionLabel>Authenticity</SectionLabel>
            <h2 className="font-serif font-light tracking-tight leading-[1.04] text-4xl md:text-5xl lg:text-[3.4rem] text-[color:var(--relumia-ink)]">
              Genuine batches, <span className="italic text-gold">verified</span>.
            </h2>
          </div>
          <p className="text-[15px] leading-relaxed text-[color:var(--relumia-muted)] max-w-lg">
            To protect our customers and doctors from counterfeits, we publicly
            list every Relumia batch currently manufactured and in circulation.
            Always cross-check the batch number, expiry and MRP printed on your
            pack against the details below. If they don&apos;t match, it is not a
            genuine JAKS Pharma product.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {BATCHES.map((b, i) => (
            <div
              key={b.batch}
              data-testid={`batch-card-${i}`}
              className="relative bg-surface border border-champagne rounded-sm p-8 md:p-10 overflow-hidden"
            >
              <div className="absolute top-6 right-6 flex items-center gap-2 rounded-full bg-[color:var(--relumia-ink)] px-3 py-1.5">
                <ShieldCheck size={14} className="text-gold" strokeWidth={2} />
                <span className="text-[11px] tracking-wide text-white uppercase">Genuine</span>
              </div>

              <div className="overline mb-2">Batch Number</div>
              <RollingText
                text={b.batch}
                testid={`batch-number-${i}`}
                className="text-4xl md:text-5xl mb-8"
              />

              <div className="grid grid-cols-3 gap-4 border-t border-champagne pt-6">
                <div>
                  <CalendarClock size={18} className="text-gold mb-2" strokeWidth={1.5} />
                  <div className="overline mb-1">Expiry</div>
                  <div className="text-[15px] font-medium text-[color:var(--relumia-ink)]">{b.exp}</div>
                </div>
                <div>
                  <BadgeIndianRupee size={18} className="text-gold mb-2" strokeWidth={1.5} />
                  <div className="overline mb-1">MRP</div>
                  <div className="text-[15px] font-medium text-[color:var(--relumia-ink)]">₹ {b.mrp}</div>
                </div>
                <div>
                  <PackageCheck size={18} className="text-gold mb-2" strokeWidth={1.5} />
                  <div className="overline mb-1">Pack</div>
                  <div className="text-[15px] font-medium text-[color:var(--relumia-ink)]">{b.pack}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5 bg-[color:var(--relumia-ink)] rounded-sm px-8 py-7">
          <div className="flex items-start gap-4">
            <ShieldCheck className="text-gold shrink-0 mt-0.5" size={24} strokeWidth={1.5} />
            <p className="text-[14px] leading-relaxed text-white/80 max-w-2xl">
              Suspect a counterfeit or found a batch not listed here? Report it to
              us immediately — your vigilance keeps every patient safe.
            </p>
          </div>
          <a
            href={CONTACT.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="report-counterfeit-btn"
            className="inline-flex items-center gap-2.5 rounded-full bg-white px-6 py-3 text-[13px] tracking-wide text-[color:var(--relumia-ink)] hover:bg-[color:var(--relumia-gold)] hover:text-white transition-colors whitespace-nowrap"
          >
            <MessageCircle size={16} strokeWidth={1.8} />
            Report / Verify a batch
          </a>
        </div>
      </div>
    </section>
  );
}
